import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { DatePicker, InputNumber, Radio, Select } from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import classNames from 'classnames';
import moment, { type Moment } from 'moment-timezone';

import Title from '@src/components/BoxContainer/components/Title';

import { clampCalendarRangeToToday, DateUtil } from '@src/packages/utils/dates';
import IntlMessages from '@src/util/IntlMessages';

import type {
  PeriodDateType,
  PeriodPresetId,
  RelativeAbsolutePeriodFilterProps,
  RelativePeriodValue,
} from './RelativeAbsolutePeriodFilter.types';
import { PERIOD_DATE_TYPES } from './RelativeAbsolutePeriodFilter.types';
import { PERIOD_PRESET_IDS } from './RelativeAbsolutePeriodFilter.types';
import { resolvePeriodPreset } from './periodPresets';
import {
  clampRangeToSpan,
  getAllowedDateTypes,
  getMaxRelativeAmount,
  isCandidateOutsideSpan,
  isRangeWithinSpan,
} from './periodLimit';
import {
  createDefaultAbsoluteRange,
  DEFAULT_RELATIVE_PERIOD,
  isRelativePeriodValue,
  toAbsolutePeriodValue,
} from './resolvePeriodToMillis';

import styles from './styles.module.less';

const { RangePicker } = DatePicker;
const { Option } = Select;
type RelativeOption = PeriodPresetId | 'custom';

const RelativeAbsolutePeriodFilterComponent: React.FC<RelativeAbsolutePeriodFilterProps> = ({
  value,
  onChange,
  label,
  dateTypes = PERIOD_DATE_TYPES,
  minAmount = 1,
  disabledDate,
  showTime = false,
  format = DateUtil.DEFAULT_DATE_FORMAT,
  allowClear = true,
  defaultAbsoluteRange = createDefaultAbsoluteRange,
  className,
  disabled = false,
  maxSpan,
  showPresets = false,
  historicalOnly = false,
  timeZone,
}) => {
  // First end the user picked while the calendar is open: the other end is limited around it.
  const [calendarAnchor, setCalendarAnchor] = useState<Moment | null>(null);
  const isRelative = isRelativePeriodValue(value);
  const mode = isRelative ? 'relative' : value.mode === 'preset' ? 'relative' : 'absolute';
  const selectedPreset: PeriodPresetId =
    !isRelative && value.mode === 'preset' ? (value.presetId ?? 'currentMonth') : 'currentMonth';
  const [customPeriod, setCustomPeriod] = useState<RelativePeriodValue>(() =>
    isRelative ? value : DEFAULT_RELATIVE_PERIOD,
  );
  const [relativeOption, setRelativeOption] = useState<RelativeOption>(() =>
    isRelative ? 'custom' : value.mode === 'preset' ? selectedPreset : 'currentMonth',
  );
  const selectedRelativeOption = isRelative
    ? 'custom'
    : value.mode === 'preset'
      ? selectedPreset
      : relativeOption;
  const relativeAmount = isRelative ? value.relativeAmount : customPeriod.relativeAmount;
  const dateType: PeriodDateType = isRelative
    ? (value.dateType ?? DEFAULT_RELATIVE_PERIOD.dateType)
    : (value.dateType ?? customPeriod.dateType);

  useEffect(() => {
    if (isRelativePeriodValue(value)) {
      setCustomPeriod(value);
      setRelativeOption('custom');
    } else if (value.mode === 'preset') {
      setRelativeOption(value.presetId ?? 'currentMonth');
    }
  }, [value]);
  const availableDateTypes = useMemo(
    () => getAllowedDateTypes(dateTypes, maxSpan),
    [dateTypes, maxSpan],
  );
  const maxAmount = maxSpan ? getMaxRelativeAmount(dateType, maxSpan) : undefined;
  const clampAmount = (amount: number, type: PeriodDateType) =>
    maxSpan ? Math.max(minAmount, Math.min(amount, getMaxRelativeAmount(type, maxSpan))) : amount;

  const handlePresetChange = (preset: PeriodPresetId) => {
    setRelativeOption(preset);
    onChange({
      isRelative: false,
      dateRange: resolvePeriodPreset(preset, undefined, timeZone, maxSpan, historicalOnly),
      dateType: 'day',
      mode: 'preset',
      presetId: preset,
    });
  };

  const handleRelativeOptionChange = (option: RelativeOption) => {
    setRelativeOption(option);
    if (option === 'custom') {
      onChange(customPeriod);
      return;
    }

    handlePresetChange(option);
  };

  const handleCustomPeriodChange = (period: RelativePeriodValue) => {
    setCustomPeriod(period);
    onChange(period);
  };

  const handleModeChange = (event: RadioChangeEvent) => {
    const nextMode = showPresets
      ? (event.target.value as 'relative' | 'absolute')
      : event.target.value === true
        ? 'relative'
        : 'absolute';
    if (nextMode === 'relative') {
      if (showPresets) {
        handleRelativeOptionChange(relativeOption);
      } else {
        onChange({ isRelative: true, relativeAmount, dateType });
      }
      return;
    }

    onChange(
      !isRelative && value.mode === 'preset'
        ? { ...value, mode: 'absolute', presetId: undefined }
        : { ...toAbsolutePeriodValue(defaultAbsoluteRange(), dateType), mode: 'absolute' },
    );
  };

  const handleAmountChange = (nextAmount: number | null) => {
    if (nextAmount == null || !isRelative) return;

    handleCustomPeriodChange({
      isRelative: true,
      relativeAmount: clampAmount(nextAmount, dateType),
      dateType,
    });
  };

  const handleDateTypeChange = (nextDateType: PeriodDateType) => {
    if (isRelative) {
      handleCustomPeriodChange({
        isRelative: true,
        relativeAmount: clampAmount(relativeAmount, nextDateType),
        dateType: nextDateType,
      });
      return;
    }

    onChange({
      isRelative: false,
      dateRange:
        maxSpan && value.dateRange
          ? clampRangeToSpan(value.dateRange, nextDateType, maxSpan)
          : value.dateRange,
      dateType: nextDateType,
    });
  };

  const handleCalendarChange = (
    dates: [Moment | null, Moment | null] | null,
    _formatted: [string, string],
    info: { range: 'start' | 'end' },
  ) => {
    // The end just picked limits the other one, even when the other still holds the previous range.
    setCalendarAnchor(dates?.[info.range === 'start' ? 0 : 1] ?? null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) setCalendarAnchor(null);
  };

  const isDateDisabled = useCallback(
    (date: Moment) =>
      (historicalOnly && date.format('YYYY-MM-DD') >
        (timeZone ? moment.tz(timeZone) : moment()).format('YYYY-MM-DD')) ||
      Boolean(disabledDate?.(date)) ||
      Boolean(
        maxSpan &&
        calendarAnchor &&
        isCandidateOutsideSpan(date, calendarAnchor, dateType, maxSpan),
      ),
    [disabledDate, maxSpan, calendarAnchor, dateType, historicalOnly, timeZone],
  );

  const handleDateRangeChange = (dates: [Moment, Moment] | null) => {
    // Picking a new start next to the previous end can exceed the cap: wait for the user to pick
    // the other end (already limited) instead of committing it.
    if (maxSpan && dates?.[0] && dates[1] && !isRangeWithinSpan(dates, dateType, maxSpan)) return;

    const range: [Moment, Moment] | null = dates?.[0] && dates[1] ? [dates[0], dates[1]] : null;
    setCalendarAnchor(null);
    onChange({
      isRelative: false,
      dateRange: historicalOnly && range ? clampCalendarRangeToToday(range, timeZone) : range,
      dateType,
      mode: 'absolute',
    });
  };

  const relativeControls = (
    <>
      <InputNumber
        className={styles.control}
        min={minAmount}
        max={maxAmount}
        value={relativeAmount}
        onChange={handleAmountChange}
        disabled={disabled}
      />
      <Select<PeriodDateType>
        className={styles.control}
        value={dateType}
        onChange={handleDateTypeChange}
        dropdownMatchSelectWidth={false}
        disabled={disabled}
      >
        {availableDateTypes.map(type => (
          <Option key={type} value={type}>
            <IntlMessages id={`dateTypes.${type}`} values={{ amount: relativeAmount }} />
          </Option>
        ))}
      </Select>
      <p className={styles.hint}>
        <IntlMessages id="dashboards.schedules.beforeUntilToday" />
      </p>
      {maxAmount != null ? (
        <p className={styles.limitHint}>
          <IntlMessages
            id="filter.period.maxRelative"
            values={{ amount: maxAmount, unit: dateType }}
          />
        </p>
      ) : null}
    </>
  );

  return (
    <div className={classNames(styles.root, className)}>
      <Title.LabelForm className="gx-mb-0" value={label ?? <IntlMessages id="filter.period" />} />

      <Radio.Group
        className={styles.modeGroup}
        options={[
          ...(showPresets
            ? [{ label: <IntlMessages id="dashboards.schedules.absolute" />, value: 'absolute' }]
            : []),
          {
            label: <IntlMessages id="filter.period" />,
            value: showPresets ? 'relative' : true,
          },
          ...(!showPresets
            ? [{ label: <IntlMessages id="dashboards.schedules.absolute" />, value: false }]
            : []),
        ]}
        optionType="button"
        buttonStyle="solid"
        value={showPresets ? mode : isRelative}
        onChange={handleModeChange}
        disabled={disabled}
      />

      {showPresets && mode === 'relative' ? (
        <>
          <Select<RelativeOption>
            className={styles.control}
            value={selectedRelativeOption}
            onChange={handleRelativeOptionChange}
            dropdownMatchSelectWidth={false}
            disabled={disabled}
          >
            {PERIOD_PRESET_IDS.map(preset => (
              <Option key={preset} value={preset}>
                <IntlMessages id={`filter.period.preset.${preset}`} />
              </Option>
            ))}
            <Option key="custom" value="custom">
              <IntlMessages id="filter.period.mode.custom" />
            </Option>
          </Select>
          {selectedRelativeOption === 'custom' ? relativeControls : null}
        </>
      ) : isRelative ? (
        relativeControls
      ) : (
        <>
          <RangePicker
            className={styles.control}
            value={value.dateRange ?? undefined}
            picker={showTime || dateType === 'day' ? 'date' : dateType}
            onChange={handleDateRangeChange}
            disabledDate={isDateDisabled}
            onCalendarChange={handleCalendarChange}
            onOpenChange={handleOpenChange}
            showTime={showTime}
            format={format}
            allowClear={allowClear}
            disabled={disabled}
          />
          {maxSpan ? (
            <p className={styles.limitHint}>
              <IntlMessages
                id="filter.period.maxRange"
                values={{ amount: maxSpan.amount, unit: maxSpan.unit }}
              />
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

const RelativeAbsolutePeriodFilter = memo(RelativeAbsolutePeriodFilterComponent);

RelativeAbsolutePeriodFilter.displayName = 'RelativeAbsolutePeriodFilter';

export default RelativeAbsolutePeriodFilter;
